import React, { useState, useEffect, useRef, useCallback } from "react";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import { Grid, Button } from "@mui/material";
import TableHead from "@mui/material/TableHead";
import ViewMore from "../viewmore/ViewMore";
import TableRow from "@mui/material/TableRow";
import ArrowDownwardOutlinedIcon from "@mui/icons-material/ArrowDownwardOutlined";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ReportGmailerrorredIcon from "@mui/icons-material/ReportGmailerrorred";
import HistoryIcon from "@mui/icons-material/History";
import moment from "moment";
import { fetchShipments } from "../../api/shipmentTable";
import { useTheme } from "@mui/material/styles";
import config from "../../api/config";
import InfiniteScroll from "react-infinite-scroll-component";
import {
	formatDeliveryStatus,
	formatMultipleValues,
	convertUTCTimeToLocalTime,
	roundToTwoDecimalPlaces,
	removeDuplicateShipments,
	getMissedRowBackground,
	normalizeKeyWithoutSpace,
	formatTimestampText,
} from "../../utilities/shipmentTable";
import { useToastContext } from "../../context/toast";
import NotesField from "../Reusables/Note";
import { CRITICALITY_COLOR_MAP } from "../../constants/ShipmentConstants";
import ReusableField from "../Reusables/ReusableField";
import { AuditHistory } from "../audit-history/audit-history";
import { fetchShipmentHistory } from "../../api/updateShipmentTable";
import { useUserContext } from "../../context/UserContext";
import TruncatedText from "../../utilities/TruncatedText";
import { getDisplayRegion } from "../../utilities/shipmentTable";

function Row(props) {
	const {
		row,
		index,
		handleSave,
		loggedInUser,
		columns,
		isThirdPartyUpdate,
		setIsThirdPartyUpdate,
	} = props;

	const formattedLocalUpdatedTime = convertUTCTimeToLocalTime(
		row?.UpdatedTime || row.ShipmentDetails.UpdatedTime || row.ShipmentDetails?.CreatedTime,
	);

	const [open, setOpen] = useState(false);
	const [viewMorePopupOpen, setviewMorePopupOpen] = useState(false);
	const [isAuditHistoryModalOpen, setIsAuditHistoryModalOpen] = useState(false);
	const [isAuditHistoryLoading, setIsAuditHistoryLoading] = useState(false);
	const [shipmentHistory, setShipmentHistory] = useState([]);
	const theme = useTheme();

	const handleViewMorePopupOpen = () => {
		setviewMorePopupOpen(true);
	};

	const handleViewMorePopupClose = () => {
		setviewMorePopupOpen(false);
	};

	const missedRowBg = getMissedRowBackground(row);

	const formatShipmentsHeaderValue = (columnId, row) => {
		const shipmentDetailsColumns = ["OrgName", "Carrier"];
		const CriticalityIconColor =
			CRITICALITY_COLOR_MAP[normalizeKeyWithoutSpace(row.ShipmentDetails.Criticality)] || "inherit";
		const CriticalityLabel = row.ShipmentDetails.Criticality || "";

		switch (columnId) {
			case "DeliveryNumber":
				return formatMultipleValues(row.DeliveryNumber);
			case "PONumber":
				return formatMultipleValues(row.ShipmentDetails?.PONumber);
			case "DeliveryAppointment":
				const appointmentDate = row.DeliveryAppointment;
				return appointmentDate ? (
					<Box style={{ display: "flex", alignItems: "center" }}>
						<div style={{ marginRight: "10px" }}>
							{moment(appointmentDate, "YYYYMMDDHHmmss").format("MM/DD/YYYY")}
						</div>
						<div>{moment(appointmentDate, "YYYYMMDDHHmmss").format("HH:mm")}</div>
					</Box>
				) : (
					""
				);
			case "ShipDate":
				const appointmentShipDate = row.ShipmentDetails.ShipDate;
				return appointmentShipDate ? (
					<Box style={{ display: "flex", alignItems: "center" }}>
						<div style={{ marginRight: "10px" }}>
							{moment(appointmentShipDate, "YYYYMMDDHHmmss").format("MM/DD/YYYY")}
						</div>
						<div>{moment(appointmentShipDate, "YYYYMMDDHHmmss").format("HH:mm")}</div>
					</Box>
				) : (
					""
				);
			case "DeliveryEta":
				return formatTimestampText(row.DeliveryEta);
			case "PickupAppointment":
				return formatTimestampText(row.ShipmentDetails.PickupAppointment);
			case "RescheduleDate":
				return formatTimestampText(row.RescheduleDate);
			case "Customer":
				return row.CustomerName;
			case "CustomerRegion":
				return getDisplayRegion(row.ShipmentDetails.CustomerRegion);
			case "ShipmentType":
				return row.ShipmentType || "Customer Deliveries";
			case "NBNumber":
				return row.NBNumber;
			case "State":
				return row.State;
			case "Distance":
				return roundToTwoDecimalPlaces(row.ShipmentDetails?.Distance);
			case "IsPreload":
				return row.ShipmentDetails?.Preload || "";
			case "DeliveryStatus":
				return formatDeliveryStatus(row.DeliveryStatus);
			case "Criticality":
				return (
					<Box sx={{ display: "flex", alignItems: "center" }}>
						<ReportGmailerrorredIcon
							fontSize="small"
							sx={{
								color: CriticalityIconColor,
								marginRight: "4px",
							}}
						/>
						<span>{CriticalityLabel}</span>
					</Box>
				);
			default:
				// Check if columnId is in the shipmentDetailsColumns array
				if (shipmentDetailsColumns.includes(columnId)) {
					return row.ShipmentDetails[columnId];
				}
				return row[columnId];
		}
	};

	// Calculate dynamic width based on the number of columns
	const columnWidth = `${(100 / columns.length).toFixed(2)}%`;

	const deliveryTimeFields = [
		{ label: "214", value: row["214DeliveryTime"], fieldType: "timefield" },
		{ label: "Four Kites", value: row.FourkitesDeliveryTime, fieldType: "timefield" },
		{ label: "Customer Portal", value: row.CustomerPortalDeliveryTime, fieldType: "timefield" },
		{ label: "Manual", value: row.DeliveryTime, fieldType: "timefield" },
		{ label: "Portal Confirmed", value: row.PortalConfirmedDeliveryTime, fieldType: "timefield" },
	];

	const handleShowHistory = async () => {
		setIsAuditHistoryModalOpen(true);
		setIsAuditHistoryLoading(true);
		const history = await fetchShipmentHistory(row.NBNumber);
		setShipmentHistory(history);
		setIsAuditHistoryLoading(false);
	};

	const onAuditHistoryModalClose = () => {
		setIsAuditHistoryModalOpen(false);
		setShipmentHistory([]);
	};

	return (
		<>
			<TableRow
				sx={{
					"& > *": {
						backgroundColor: missedRowBg ? missedRowBg : theme.palette.primary.secondary,
					},
				}}
			>
				{columns.map((column, columnIndex) => {
					const isFirstColumn = columnIndex === 0;

					return (
						<TableCell
							key={column.id}
							sx={{
								width: columnWidth, // Use dynamic width
								padding: "0", // Reduced padding
								textAlign: "left",
								whiteSpace: "nowrap",
								overflow: "hidden",
								fontSize: "12px", // Reduced font size
							}}
						>
							{isFirstColumn ? (
								<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
									<IconButton
										size="small"
										onClick={() => setOpen(!open)}
										sx={{ marginRight: "8px", padding: "0px" }}
									>
										{open ? (
											<KeyboardArrowUpIcon fontSize="small" />
										) : (
											<KeyboardArrowDownIcon fontSize="small" />
										)}
									</IconButton>
									<span style={{ minWidth: "100px", textAlign: "left" }}>
										{formatShipmentsHeaderValue(column.id, row)}
									</span>
								</Box>
							) : [
									"DeliveryNumber",
									"PONumber",
									"DeliveryAppointment",
									"ShipDate",
									"Criticality",
							  ].includes(column.id) ? (
								formatShipmentsHeaderValue(column.id, row)
							) : (
								<TruncatedText text={String(formatShipmentsHeaderValue(column.id, row) ?? "")} />
							)}
						</TableCell>
					);
				})}
			</TableRow>
			<TableRow
				sx={{
					"& > *": {
						backgroundColor: missedRowBg ? missedRowBg : theme.palette.secondary.main,
					},
				}}
			>
				<TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={columns.length}>
					<Collapse in={open} timeout="auto" unmountOnExit>
						<Box
							sx={{
								marginLeft: "200px",
								marginTop: "20px",
							}}
						>
							<Grid container spacing={2}>
								{[
									["NB Number", row.NBNumber],
									["Preload", row.ShipmentDetails.Preload],
									["Transit Time", row.ShipmentDetails.TransitTime],
									["Distance", roundToTwoDecimalPlaces(row.ShipmentDetails.Distance)],
									["Pick-up ETA", row.ShipmentDetails.PickupEta],
									["Pick-up Status", row.PickupStatus],
									["State", row.State],
									["Pick-up Appointment", row.ShipmentDetails.PickupAppointment],
									["Delivery ETA", row.DeliveryEta],
									["Reason Code", row.ReasonCode],
									["Source Location Name", row.ShipmentDetails?.SourceLocationName],
									["Source Location ID", row.ShipmentDetails?.SourceLocationGID],
									["Destination Location Name", row.ShipmentDetails?.DestinationLocationName],
									["SPOT", row.ShipmentDetails?.SPOT],
									["DTS Location Type", row.ShipmentDetails?.DTSLocationType],
									["EBS Sales Order Number", row.ShipmentDetails?.EBS_Sales_Order_Number],
									["Reschedule Date", row?.RescheduleDate || ""],
									[
										"Tracker",
										row?.UpdatedBy ||
											row.ShipmentDetails?.UpdatedBy ||
											row.ShipmentDetails?.CreatedBy,
									],
									["Last Update Time", formattedLocalUpdatedTime],
									["Pick-up Time", row.ShipmentDetails.PickupTime],
									["Notes", row.Notes],
									["Customer Notes", row?.ShipmentDetails?.customerNotes?.trim() || ""],
									["Warehouse", row.WareHouseNbr],
									["Comment", row.Comment],
								].map(
									([title, value]) =>
										columns.findIndex((k) => k.label === title) === -1 && (
											<Grid item xs={12} sm={6} md={5} lg={3} key={title}>
												<Box
													sx={{
														width: "100%",
														boxSizing: "border-box",
														marginBottom: "8px",
														display: "flex",
														flexDirection: "column",
														alignItems: "flex-start",
														justifyContent: "center",
													}}
												>
													<Typography
														sx={{
															fontWeight: 500,
															fontFamily: "Poppins, sans-serif",
															lineHeight: "24px",
															textAlign: "left",
															whiteSpace: "normal",
															wordBreak: "break-word",
															verticalAlign: "top",
															fontSize: "13px", // Reduced font size
														}}
													>
														{title}
													</Typography>

													{title === "Notes" ? (
														<NotesField text={value} isUserLoggedin={true} />
													) : (
														<Typography
															sx={{
																fontFamily: "Poppins, sans-serif",
																fontWeight: 400,
																lineHeight: "24px",
																letterSpacing: "0.5px",
																textAlign: "left",
																whiteSpace: "normal",
																wordBreak: "break-word",
																verticalAlign: "top",
																fontSize: "13px", // Reduced font size
															}}
														>
															{[
																"Pick-up ETA",
																"Pick-up Appointment",
																"Delivery ETA",
																"Delivery Time",
																"Reschedule Date",
																"Pick-up Time",
															].includes(title)
																? formatTimestampText(value)
																: value}
														</Typography>
													)}
												</Box>
											</Grid>
										),
								)}
							</Grid>

							<Box
								sx={{
									marginTop: "20px",
									paddingBottom: "16px",
								}}
							>
								<Typography
									sx={{
										fontWeight: 600,
										fontFamily: "Poppins, sans-serif",
										fontSize: "14px",
										lineHeight: "28px",
										marginBottom: "16px",
									}}
								>
									Delivery Time:
								</Typography>

								<Grid container spacing={2}>
									{deliveryTimeFields.map(({ label, value, fieldType }) => (
										<Grid item xs={12} sm={6} md={5} lg={3} key={label}>
											<ReusableField label={label} value={value} fieldType={fieldType} />
										</Grid>
									))}
								</Grid>
							</Box>

							<ViewMore
								open={viewMorePopupOpen}
								handleClose={handleViewMorePopupClose}
								rowData={row}
								isThirdPartyUpdate={isThirdPartyUpdate}
								setIsThirdPartyUpdate={setIsThirdPartyUpdate}
								onSave={handleSave}
								loggedInUser={loggedInUser}
							/>
							{isAuditHistoryModalOpen && (
								<AuditHistory
									isLoading={isAuditHistoryLoading}
									shipmentHistory={shipmentHistory}
									handleClosePopup={onAuditHistoryModalClose}
								/>
							)}
						</Box>
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								paddingBottom: "16px",
								borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
							}}
						>
							<Button
								onClick={handleShowHistory}
								variant="text"
								sx={{
									fontFamily: "Poppins, sans-serif",
									color: theme.palette.text.button,
									marginRight: 4,
									border: theme.palette.border.main,
									"&:hover": {
										backgroundColor: theme.palette.buttonhover.main,
										color: theme.palette.text.buttonhover,
									},
									height: "30px",
									fontSize: "12px",
									borderRadius: 20,
									padding: "20px 14px 20px 12px",
									textTransform: "none",
								}}
							>
								<HistoryIcon
									sx={{
										marginRight: "3px",
										height: "16px",
										width: "16px",
									}}
								/>
								Shipment Record
							</Button>
							<Button
								onClick={handleViewMorePopupOpen}
								variant="text"
								sx={{
									fontFamily: "Poppins, sans-serif",
									color: theme.palette.text.button,
									border: theme.palette.border.main,
									"&:hover": {
										backgroundColor: theme.palette.buttonhover.main,
										color: theme.palette.text.buttonhover,
									},
									height: "30px",
									fontSize: "12px",
									textTransform: "none",
									borderRadius: 20,
									padding: "20px 14px",
								}}
							>
								View/Edit More
							</Button>
						</Box>
					</Collapse>
				</TableCell>
			</TableRow>
		</>
	);
}

export default function CollapsibleTable({
	columns,
	isSearchAction,
	appliedFilters,
	onRowsChange,
	onTotalCountChange,
	accountInfo,
	onFetchError,
	setIsSaveBtnClicked,
	availableDates,
}) {
	const [rows, setRows] = useState([]);
	const [error, setError] = useState(null);
	const [sortBy, setSortBy] = useState(null);
	const [sortOrder, setSortOrder] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [isSorting, setIsSorting] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const [page, setPage] = useState(1);
	const [totalCount, setTotalCount] = useState(0);
	const [allFetchedRows, setAllFetchedRows] = useState([]);
	const theme = useTheme();
	const { showErrorToast } = useToastContext();
	const [isThirdPartyUpdate, setIsThirdPartyUpdate] = useState(false);
	const { userDetails } = useUserContext();

	const fetchData = useCallback(
		async (isInitialFetch = false) => {
			if (!hasMore && !isInitialFetch) return;

			try {
				setIsLoading(true);
				const filters = appliedFilters || {};

				const data = await fetchShipments(filters, sortBy, sortOrder, isInitialFetch ? 1 : page, availableDates, isSearchAction, userDetails);
				const allShipments = (data.shipments || []).map(s => ({ ...s, ShipmentType: s.DataSource === "Raw Materials and Transfers" ? "Raw Materials and Transfers" : "Customer Deliveries" }));
				const combinedHasMore = data.hasMore;
				const combinedTotalCount = data.totalCount || 0;
				const nextPage = data.nextPage || null;

				onFetchError(false);
				setRows((prevRows) => {
					if (isInitialFetch) {
						const uniqueShipments = removeDuplicateShipments(allShipments);
						setAllFetchedRows(uniqueShipments);
						onRowsChange(uniqueShipments);
						return uniqueShipments;
					}

					const combinedShipments = [...prevRows, ...allShipments];
					const uniqueShipments = removeDuplicateShipments(combinedShipments);
					setAllFetchedRows(uniqueShipments);
					onRowsChange(uniqueShipments);
					return uniqueShipments;
				});

				setHasMore(combinedHasMore);
				setPage(nextPage);
				setError(null);
				setTotalCount(combinedTotalCount);
				onTotalCountChange && onTotalCountChange(combinedTotalCount);
			} catch (error) {
				console.error("Error fetching shipments:", error);
				onFetchError(true);
				setError(
					error.response?.status === 404
						? "No shipments are available"
						: "Failed to fetch shipments",
				);
				setHasMore(false);
			} finally {
				setIsLoading(false);
				setIsSorting(false);
			}
		},
		[appliedFilters, sortBy, sortOrder, page, hasMore, accountInfo],
	);

	useEffect(() => {
		if (appliedFilters || sortBy || sortOrder) {
			setRows([]);
			setPage(1);
			setHasMore(true);
			setTotalCount(0);
			setError(null);
			fetchData(true);
		}
	}, [appliedFilters, sortBy, sortOrder]);

	useEffect(() => {
		const ws = new WebSocket(config.websocketURL, [userDetails.userIdToken]);

		ws.onopen = () => {
			console.log("Connected to WebSocket server");
		};

		ws.onmessage = (event) => {
			const receivedData = JSON.parse(event.data);

			if (Array.isArray(receivedData)) {
				setError(null);
				const filters = appliedFilters || {};

				setRows((prevRows) => {
					let updatedRows = [...prevRows];

					receivedData.forEach((incomingData) => {
						// Check if incomingData matches filters before adding/updating
						const matchesFilters = Object.entries(filters).every(([key, value]) => {
							if (value === undefined || value === null || value === "") return true;
							if (key.startsWith("ShipmentDetails.")) {
								const subKey = key.replace("ShipmentDetails.", "");
								return (
									incomingData.ShipmentDetails && incomingData.ShipmentDetails[subKey] === value
								);
							}
							return incomingData[key] === value;
						});

						if (!matchesFilters) return; // Skip if doesn't match filters

						const existingRowIndex = updatedRows.findIndex(
							(row) =>
								row.DeliveryNumber === incomingData.DeliveryNumber &&
								row.NBNumber === incomingData.NBNumber &&
								// RM/transfer trackers have no StopSequence; match on NBNumber+DeliveryNumber alone.
								((!row.StopSequence && !incomingData.StopSequence) ||
									row.StopSequence === incomingData.StopSequence),
						);

						if (existingRowIndex !== -1) {
							// Update existing row
							updatedRows[existingRowIndex] = {
								...updatedRows[existingRowIndex],
								...incomingData,
								ShipmentDetails: incomingData.ShipmentDetails,
							};
						} else {
							// Add new row
							updatedRows.push(incomingData);
						}
					});

					return updatedRows;
				});
			}
		};

		ws.onerror = (error) => {
			console.error("WebSocket error:", error);
		};

		return () => {
			ws.close();
		};
	}, [appliedFilters, userDetails]);

	const handleSort = useCallback(
		(columnId) => {
			if (isSorting) return;
			setIsSorting(true);
			if (sortBy === columnId) {
				setSortOrder(sortOrder === "ascending" ? "descending" : "ascending");
			} else {
				setSortBy(columnId);
				setSortOrder("ascending");
			}
		},
		[sortBy, sortOrder, isSorting],
	);

	const handleSave = async (updatedRowData) => {
		setIsSaveBtnClicked(true);
		try {
			// Update the rows state with the updated rowData
			setRows((prevRows) =>
				prevRows.map((row) => (row.NBNumber === updatedRowData.NBNumber ? updatedRowData : row)),
			);

			// Fetch the updated data from the server
			// const filters = appliedFilters || {};
			// setIsLoading(true);
			// const data = await fetchShipments(filters, sortBy, sortOrder);
			// setRows(data);
			// setError(null);
		} catch (error) {
			console.error("Error fetching shipments:", error);
			setError("Failed to fetch shipments");
		} finally {
			setIsLoading(false);
		}
	};

	// Calculate dynamic width based on the number of columns
	const columnWidth = `${(100 / columns.length).toFixed(2)}%`;

	return (
		<Paper
			sx={{
				overflow: "hidden",
				margin: "20px",
				marginBottom: 0,
				display: "flex",
				flexDirection: "column",
				flex: 1,
				minHeight: 0,
			}}
		>
			{error ? (
				<Typography
					sx={{
						padding: "10px",
						fontSize: "20px",
						fontWeight: "medium",
						textAlign: "center",
						color: theme.palette.text.primary,
					}}
				>
					No shipments are available
				</Typography>
			) : (
				<Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
					<Box sx={{ overflow: "auto", position: "relative" }}>
						<Table
							aria-label="collapsible table"
							stickyHeader
							sx={{ tableLayout: "fixed", width: "100%" }}
						>
							<TableHead>
								<TableRow>
									{columns.map((column, columnIndex) => {
										const isFirstColumn = columnIndex === 0;

										return (
											<TableCell
												key={column.id}
												sx={{
													fontWeight: "bold",
													width: columnWidth,
													backgroundColor: theme.palette.background.paper,
													zIndex: 1,
													padding: "4px", // Reduced padding
													borderBottom: `1px solid ${theme.palette.divider}`,
													textAlign: isFirstColumn ? "center" : "left",
													fontSize: "12px", // Reduced font size
												}}
											>
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
														justifyContent: isFirstColumn ? "center" : "flex-start",
													}}
												>
													<span>{column.label}</span>
													{column.sortable && (
														<ArrowDownwardOutlinedIcon
															sx={{
																color: isSorting ? "#ccc" : "#006098",
																fontSize: "16px",
																cursor: isSorting ? "not-allowed" : "pointer",
																transform:
																	sortBy === column.id && sortOrder === "ascending"
																		? "rotate(180deg)"
																		: "rotate(0deg)",
																marginLeft: "4px",
															}}
															onClick={() => !isSorting && handleSort(column.id)}
														/>
													)}
												</Box>
											</TableCell>
										);
									})}
								</TableRow>
							</TableHead>
						</Table>
					</Box>
					<Box
						sx={{ overflowY: "auto", overflowX: "hidden", flex: 1, minHeight: 0 }}
						id="scrollableDiv"
					>
						<InfiniteScroll
							dataLength={rows.length}
							next={() => fetchData(false)}
							hasMore={hasMore}
							loader={
								<Typography sx={{ textAlign: "center", padding: "16px" }}>Loading...</Typography>
							}
							scrollableTarget="scrollableDiv"
							initialLoad={false}
						>
							<Table sx={{ tableLayout: "fixed", width: "100%" }}>
								<TableBody>
									{rows.map((row, index) => (
										<Row
											key={`${row.DeliveryNumber}-${index}`}
											row={row}
											index={index}
											handleSave={handleSave}
											loggedInUser={accountInfo?.account?.name}
											columns={columns}
											setIsThirdPartyUpdate={setIsThirdPartyUpdate}
											isThirdPartyUpdate={isThirdPartyUpdate}
										/>
									))}
								</TableBody>
							</Table>
						</InfiniteScroll>
					</Box>
					{rows.length > 0 && (
						<Typography sx={{ textAlign: "center", padding: "4px", fontSize: ".75rem" }}>
							Showing {rows.length} of {totalCount} shipments
						</Typography>
					)}
				</Box>
			)}
			{isLoading && rows.length === 0 && (
				<Typography sx={{ textAlign: "center", padding: "16px" }}>Loading...</Typography>
			)}
		</Paper>
	);
}
